import { useEffect, useState } from "react";
import { Button, CircularProgress, Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { CrawlItem, CrawlItemPreview } from "../../../common/interfaces/admin/crawl.interface";
import { fetchCrawlItemPreview } from "../../../api/admin/content-providers/endpoints-referrers";
import { kindLabel, previewDocument, reasonLabel } from "./review-crawl.util";

/**
 * Preview drawer (web_scraper_provider_design.md §10, D16): the cleaned
 * snapshot in a sandboxed iframe with no permissions and a CSP that forbids
 * every load, a Source toggle that shows the raw HTML as text only, the live
 * link, and the include / exclude switch for the item being looked at.
 */
export const CrawlPreviewDrawer = ({
    companyId,
    providerId,
    runId,
    item,
    canDecide,
    busy,
    handleClose,
    handleDecision
}: {
    companyId: number;
    providerId: number;
    runId: string;
    item: CrawlItem | null;
    canDecide: boolean;
    busy: boolean;
    handleClose: () => void;
    handleDecision: (item: CrawlItem, decision: "include" | "exclude") => void;
}) => {
    const [variant, setVariant] = useState<"snapshot" | "raw">("snapshot");
    const [preview, setPreview] = useState<CrawlItemPreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setVariant("snapshot");
    }, [item?.id]);

    useEffect(() => {
        if (!item) {
            setPreview(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError("");
        fetchCrawlItemPreview(companyId, providerId, runId, item.id, variant)
            .then(response => {
                if (!cancelled) setPreview(response.data);
            })
            .catch(() => {
                if (!cancelled) setError("The preview could not be loaded.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [companyId, providerId, runId, item, variant]);

    const title = item?.title || item?.name || item?.canonicalUrl || "";

    return (
        <Drawer
            anchor="right"
            open={!!item}
            onClose={handleClose}
            PaperProps={{
                className: "crawl-preview",
                role: "dialog",
                "aria-label": item ? `Preview of ${title}` : "Preview"
            }}
        >
            {item && (
                <>
                    <div className="crawl-preview__header">
                        <div className="crawl-preview__heading">
                            <span className="crawl-preview__kind">{kindLabel(item.kind)}</span>
                            <span className="crawl-preview__title" role="heading" aria-level={2}>
                                {title}
                            </span>
                            <a
                                className="crawl-preview__url"
                                href={item.canonicalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {item.canonicalUrl}
                            </a>
                        </div>
                        <IconButton aria-label="Close preview" onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div className="crawl-preview__toolbar">
                        {item.kind === "page" && (
                            <div
                                className="crawl-preview__toggle"
                                role="group"
                                aria-label="Preview mode"
                            >
                                <Button
                                    variant="text"
                                    className={
                                        variant === "snapshot" ? "primary-button" : "thirt-button"
                                    }
                                    aria-pressed={variant === "snapshot"}
                                    onClick={() => setVariant("snapshot")}
                                >
                                    Snapshot
                                </Button>
                                <Button
                                    variant="text"
                                    className={
                                        variant === "raw" ? "primary-button" : "thirt-button"
                                    }
                                    aria-pressed={variant === "raw"}
                                    onClick={() => setVariant("raw")}
                                >
                                    Source
                                </Button>
                            </div>
                        )}
                        <Button
                            variant="text"
                            className="thirt-button"
                            component="a"
                            href={item.canonicalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Open live page
                        </Button>
                        {canDecide && (
                            <Button
                                variant="text"
                                className="primary-button"
                                disabled={busy}
                                onClick={() =>
                                    handleDecision(
                                        item,
                                        item.decision === "include" ? "exclude" : "include"
                                    )
                                }
                            >
                                {item.decision === "include"
                                    ? "Exclude this item"
                                    : "Include this item"}
                            </Button>
                        )}
                    </div>
                    {(item.reason || item.note || item.error) && (
                        <div className="crawl-preview__note">
                            {[reasonLabel(item.reason), item.note, item.error]
                                .filter(Boolean)
                                .join(" · ")}
                        </div>
                    )}
                    <div className="crawl-preview__body">
                        {loading && (
                            <div className="crawl-preview__loading" role="status">
                                <CircularProgress size={24} /> Loading preview…
                            </div>
                        )}
                        {!loading && error && (
                            <div className="crawl-preview__error" role="alert">
                                {error}
                            </div>
                        )}
                        {!loading && !error && preview && (
                            <>
                                {preview.note && !preview.html && !preview.raw && (
                                    <p className="crawl-preview__empty">{preview.note}</p>
                                )}
                                {variant === "snapshot" && preview.html && (
                                    <iframe
                                        className="crawl-preview__frame"
                                        title={`Snapshot of ${title}`}
                                        sandbox=""
                                        referrerPolicy="no-referrer"
                                        srcDoc={previewDocument(preview.html)}
                                    />
                                )}
                                {variant === "raw" && preview.raw && (
                                    /* eslint-disable jsx-a11y/no-noninteractive-tabindex -- a scrolling source pane must be reachable by keyboard (WCAG 2.1.1) */
                                    <pre
                                        className="crawl-preview__source"
                                        tabIndex={0}
                                        role="region"
                                        aria-label="Page source"
                                    >
                                        {preview.raw}
                                    </pre>
                                    /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
        </Drawer>
    );
};

export default CrawlPreviewDrawer;
